import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {
  TestSetupComponent,
  BaseSetupTab,
  TestSetupTabsComponent,
  SaveTemplateComponent,
  TestSetupModule,
  TemplateService
} from '@nrwl/test-setup';
import { RFC2544SetupService } from './rfc2544-setup.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { get, merge } from 'lodash';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { RFC2544SetupComponent } from './tabs/rfc2544-setup/rfc2544-setup.component';
import { RFC2544EndpointsComponent } from './tabs/rfc2544-endpoints/rfc2544-endpoints.component';
import { RFC2544StreamsComponent } from './tabs/rfc2544-streams/rfc2544-streams.component';
import {
  NotificationService,
  RunTypeValue,
  TestListService,
  TestTypeValue,
  ValuesExchangeProvider,
  DialogService
} from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { RFC2544ConfigFieldMapping as ConfigFieldMapping } from './models/models';

@Component({
  selector: 'nrwl-rfc2544-setup',
  standalone: true,
  imports: [TestSetupModule],
  providers: [RFC2544SetupService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class RFC2544TestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  onClose = new EventEmitter<any>();
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  currentObj: any;
  subscription: Subscription;
  activatedRouteSubscription: Subscription;
  testId = null;
  setupId: string;
  dataId: any;
  mode: string;
  templateId: string;
  setupConfig: any;
  agentBasedConfig: any;
  router: Router;
  setupService: RFC2544SetupService;
  changeDetectorRef: ChangeDetectorRef;

  constructor(
    router: Router,
    setupService: RFC2544SetupService,
    changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private readonly navigationProvider: NavigationProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly exchange: ValuesExchangeProvider,
    private readonly templateService: TemplateService,
    private readonly testListService: TestListService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit(): void {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.templateId = defaultParams?.templateId;
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    if (!this.setupService.editMode) {
      this.setupService.currentConfig.buttonName = 'START TEST';
    }
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }
  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }
  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }
  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleFinish(): void {
    this.createRfc2544Test();
  }

  createRfc2544Test(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    this.setupService.openTestStatusDialog(
      this.setupService.editMode ? 'Updating rfc2544' : 'Creating rfc2544',
      this.setupService.editMode ? 'Update' : 'Add'
    );
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    if (!this.setupService.editMode) {
      this.subscription = this.setupService._saveControllerTest(lmapObject).subscribe(
        response => {
          const message = 'RFC2544 test has been created';
          this.notificationService.onSuccess(message);
          this.testListService.updateTestResponse();
          this.setupService.dialogRef.close();
          this.toggleClose();
          this.exchange.notifyListeners('new_test', response);
          if (this.setupService.output?.runType === RunTypeValue.ON_DEMAND) {
            this.openTestRunning(response.data.id);
          }
        },
        error => {
          const message = 'Failed to create rfc2544 test';
          this.notificationService.onFail(message);
          this.setupService.dialogRef.close();
          this.toggleClose();
        }
      );
    } else {
      this.subscription = this.setupService
        ._updateControllerTest({
          opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: 'next-run' },
          config: lmapObject
        })
        .subscribe(
          response => {
            const message = 'RFC2544 test has been updated';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
          },
          error => {
            const message = 'Failed to update rfc2544 test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
    }
  }

  openTestRunning(testId: string): void {
    this.navigationProvider.navigateToDashboard(['test-running'], {
      queryParams: {
        testId: testId,
        testName: this.setupService.output.alias,
        testType: TestTypeValue.RFC2544,
        source: null,
        layeredSource: [],
        queryParamsHandling: 'merge',
        replaceUrl: true,
        hideTestSetup: true // to hide the testsetup wizard panel
      }
    });
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    this.setupService._downloadTestConfig(lmapObject);
  }

  saveTestConfig(): void {
    this.templateService.setTemplateId(this.templateId);
    this.setupService.onNext();
    let configParams = this.setupService.getRfc2544TestConfig();
    configParams['tos-diff-serv'] =
      this.setupService.output[ConfigFieldMapping.STREAM]['tos-diff-serv'];
    const optionsParams = this.setupService.getTestOptionsForSaveTemplate();
    const otherParams = {
      [ConfigFieldMapping.SCHEDULER]: this.setupService.output[ConfigFieldMapping.SCHEDULER]
    };
    configParams = { ...configParams, ...optionsParams, ...otherParams };
    const selectedOriginator =
      this.setupService.selectedOriginator ||
      this.setupService.output[ConfigFieldMapping.SELECTED_ORIGINATOR];
    const selectedDestination =
      this.setupService.selectedDestination ||
      this.setupService.output[ConfigFieldMapping.SELECTED_DESTINATION];
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      selectedOriginator,
      selectedDestination
    );
    allTestParams['ipv6StreamConfig'] = this.setupService.getIpv6StreamConfig();
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.RFC2544 }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();

    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.RFC2544,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }

  getSetupService(): RFC2544SetupService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'rfc2544.setup.html':
        return RFC2544SetupComponent;
      case 'rfc2544.endpoints.html':
        return RFC2544EndpointsComponent;
      case 'rfc2544.streams.html':
        return RFC2544StreamsComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
